import React, { useState, useEffect } from 'react'
import styled from 'styled-components'

// Lenin campaign runs from now through end of 2026-07-10 IST (i.e. midnight 11 Jul IST).
// After that, fall back to the default Zyppys ad automatically.
const LENIN_ENDS_AT = '2026-07-11T00:00:00+05:30'

const LENIN_AD = {
    url: 'https://movies.indianclicks.com/lenin-movie',
    title: 'Lenin India & USA Theaters',
    desktopImage: '/lenin-desktop.jpg',
    mobileImage: '/lenin-mobile.jpg',
    alt: 'Lenin — Akhil Akkineni. Now in India & USA Theaters.',
    buttonText: 'Book Now',
}

const DEFAULT_AD = {
    url: 'https://zyppys.com/',
    image: '/zyppysad.png',
    alt: 'Zyppys - Premium car rentals in India, luxury travel at an affordable price',
    buttonText: 'Proceed',
}

const pickActiveAd = () => {
    if (Date.now() < new Date(LENIN_ENDS_AT).getTime()) return LENIN_AD
    return DEFAULT_AD
}

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`

const AdContainer = styled.div`
    position: relative;
    max-width: 90vw;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    align-items: center;
`

const AdTitle = styled.h2`
    color: #FFD700;
    font-size: 28px;
    font-weight: 700;
    text-align: center;
    margin: 0 0 15px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

    @media (max-width: 768px) {
        font-size: 20px;
        margin-bottom: 10px;
    }
`

const AdLink = styled.a`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-decoration: none;
    cursor: pointer;
`

const AdImage = styled.img`
    max-width: 95vw;
    max-height: 75vh;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.02);
    }

    @media (max-width: 768px) {
        max-width: 98vw;
        max-height: 70vh;
    }
`

const AdImageDesktop = styled(AdImage)`
    @media (max-width: 768px) {
        display: none;
    }
`

const AdImageMobile = styled(AdImage)`
    display: none;
    @media (max-width: 768px) {
        display: block;
    }
`

const ReadMoreButton = styled.span`
    display: inline-block;
    margin-top: 15px;
    padding: 12px 40px;
    background: linear-gradient(135deg, #FFD700, #FFA500);
    color: #000;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    border-radius: 30px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);

    &:hover {
        background: linear-gradient(135deg, #FFF, #FFD700);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
    }

    @media (max-width: 768px) {
        padding: 10px 30px;
        font-size: 14px;
    }
`

const SkipButton = styled.button`
    position: fixed;
    top: 16px;
    right: 16px;
    background-color: rgba(0, 0, 0, 0.85);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.5);
    padding: 10px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    transition: all 0.2s ease;
    z-index: 10000;

    &:hover {
        background-color: rgba(255, 255, 255, 0.25);
        border-color: rgba(255, 255, 255, 0.8);
    }

    @media (max-width: 768px) {
        top: 12px;
        right: 12px;
        padding: 8px 14px;
        font-size: 13px;
    }
`

const AdLabel = styled.span`
    position: absolute;
    top: -40px;
    left: 0;
    background-color: rgba(255, 255, 255, 0.9);
    color: #333;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
`

const AD_DURATION = 30 // seconds — auto-dismiss timeout

// Module-level flag - resets on page refresh, persists during client-side navigation
let adAlreadyShown = false

const AdInterstitial = () => {
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        // Show ad only on first render after page load/refresh
        // adAlreadyShown resets when browser refreshes (JS reloads)
        // but stays true during Gatsby client-side navigation
        if (!adAlreadyShown) {
            setIsVisible(true)
            adAlreadyShown = true
        }
    }, [])

    useEffect(() => {
        if (!isVisible) return

        const timer = setTimeout(() => {
            setIsVisible(false)
        }, AD_DURATION * 1000)

        return () => clearTimeout(timer)
    }, [isVisible])

    const handleSkip = (e) => {
        e.stopPropagation()
        setIsVisible(false)
    }

    if (!isVisible) return null

    const ad = pickActiveAd()
    const hasResponsiveImages = ad.desktopImage && ad.mobileImage

    return (
        <Overlay>
            <AdContainer>
                <AdLabel>Advertisement</AdLabel>
                <SkipButton onClick={handleSkip}>
                    Skip Ad ✕
                </SkipButton>

                <AdLink href={ad.url} target="_blank" rel="noopener noreferrer">
                    {ad.title && <AdTitle>{ad.title}</AdTitle>}
                    {hasResponsiveImages ? (
                        <>
                            <AdImageDesktop src={ad.desktopImage} alt={ad.alt} />
                            <AdImageMobile src={ad.mobileImage} alt={ad.alt} />
                        </>
                    ) : (
                        <AdImage src={ad.image} alt={ad.alt} />
                    )}
                    <ReadMoreButton>{ad.buttonText}</ReadMoreButton>
                </AdLink>
            </AdContainer>
        </Overlay>
    )
}

export default AdInterstitial
